import React, { useEffect, useState, Children, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChefHat, DollarSign, Clock, Check, X, ShoppingBag, Utensils, Droplet, CupSoda, ArrowRight, Star, ShieldCheck, Flame } from 'lucide-react';
// Animation variants
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function SalesPage() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Initialize Meta Pixel and fire PageView on load
  useEffect(() => {
    // Set document title
    document.title = 'Sabor Callejero';
    // Initialize Facebook Pixel
    if (typeof window !== 'undefined' && !(window as any).fbq) {
      const f = window as any;
      const n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      // Load the Facebook Pixel script
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
      (window as any).fbq('init', '1375884300474449');
    }
    // Track PageView
    if ((window as any).fbq) {
      ;
      (window as any).fbq('track', 'PageView');
    }
  }, []);
  // Handle Add to Cart click with Meta Pixel tracking
  const handleAddToCart = () => {
    // Fire AddToCart event
    if ((window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'Sabor Callejero - Menú Viral de Combos',
        content_type: 'product',
        value: 6.39,
        currency: 'USD'
      });
    }
  };
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-[#FFFBF0] font-sans text-gray-900 overflow-x-hidden" data-id="element-1">
      {/* Sticky Header */}
      <motion.header initial={{
      y: -100
    }} animate={{
      y: 0
    }} className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200 py-3 px-4 shadow-sm" data-id="element-2">
        <div className="max-w-6xl mx-auto flex justify-center items-center" data-id="element-3">
          <div className="font-black text-xl md:text-2xl tracking-tighter text-[#DC2626] flex items-center gap-2" data-id="element-4">
            <Flame className="w-6 h-6 fill-current" data-id="element-5" />
            SABOR CALLEJERO
          </div>
        </div>
      </motion.header>

      {/* 1. HERO SECTION */}
      <section className="relative bg-[#1F1F1F] text-white pt-12 pb-20 px-4 overflow-hidden" data-id="element-6">
        {/* Abstract background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-7"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-8"></div>

        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-9">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="space-y-6" data-id="element-10">
            <div className="inline-block bg-red-600/20 text-red-400 font-bold px-4 py-1.5 rounded-full text-sm border border-red-600/30" data-id="element-11">
              MÉTODO COMPROBADO
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight tracking-tight" data-id="element-12">
              De 0 a tus primeros pedidos en{' '}
              <span className="text-[#DC2626]" data-id="element-13">30 días</span> (sin volverte
              loca)
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed" data-id="element-14">
              Si tu sueldo no alcanza, esto es un plan... no un PDF más. No
              necesitas 350 recetas: necesitas un sistema que te diga qué
              vender, cómo producir y cómo cobrar.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-15">
            <div className="absolute inset-0 bg-gradient-to-tr from-red-600/20 to-transparent rounded-3xl transform rotate-3" data-id="element-16"></div>
            <img src="https://cdn.magicpatterns.com/uploads/31vKrbNU8aAgPVZWT6WBty/Mock_up.png" alt="Sabor Callejero Ebook en Tablet" className="relative z-10 w-full max-w-md mx-auto drop-shadow-2xl transform -rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-17" />

            {/* CTA Button positioned below image */}
            <div className="mt-8 flex flex-col items-center gap-4" data-id="element-18">
              <button onClick={scrollToOffer} className="bg-[#DC2626] hover:bg-red-700 text-white font-bold py-3 px-6 rounded-xl text-base transition-all transform hover:scale-105 shadow-xl shadow-red-900/30 flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-19">
                QUIERO MI MENÚ DE COMBOS
                <ArrowRight className="w-5 h-5" data-id="element-20" />
              </button>

              {/* Social proof below button */}
              <div className="flex items-center gap-2 text-sm text-gray-400" data-id="element-21">
                <div className="flex -space-x-2" data-id="element-22">
                  {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full bg-gray-700 border-2 border-[#1F1F1F] flex items-center justify-center text-xs" data-id="element-23">
                      <Star className="w-3 h-3 text-yellow-500 fill-current" data-id="element-24" />
                    </div>)}
                </div>
                <p data-id="element-25">Más de 500 emprendedoras ya empezaron</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. PAIN AGITATION */}
      <section className="py-20 px-4 bg-white" data-id="element-26">
        <div className="max-w-4xl mx-auto" data-id="element-27">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-12" data-id="element-28">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4" data-id="element-29">
              El problema real: estás intentando "adivinar"
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-30">
              Esa incertidumbre te drena más que la cocina. La mayoría empieza
              mal porque quiere vender "comida" sin estrategia.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-31">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} className="space-y-4" data-id="element-32">
              {['¿Qué receta vendo hoy?', '¿Cuánto cobro para no perder?', '¿Y si no me queda ganancia?', '¿Cómo entrego sin perder el día?'].map((pain, idx) => <motion.div key={idx} variants={fadeIn} className="flex items-start gap-4 p-4 bg-red-50 rounded-xl border border-red-100" data-id="element-33">
                  <div className="bg-red-100 p-2 rounded-full shrink-0" data-id="element-34">
                    <X className="w-5 h-5 text-red-600" data-id="element-35" />
                  </div>
                  <p className="font-medium text-gray-800 pt-1" data-id="element-36">{pain}</p>
                </motion.div>)}
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-gray-900 text-white p-8 rounded-2xl shadow-xl relative overflow-hidden" data-id="element-37">
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/20 rounded-full blur-2xl" data-id="element-38"></div>
              <h3 className="text-2xl font-bold mb-4 relative z-10" data-id="element-39">
                La trampa de las "350 recetas"
              </h3>
              <p className="text-gray-300 mb-6 relative z-10" data-id="element-40">
                Tener un PDF con cientos de recetas solo causa parálisis.
                Terminas con 20 ingredientes abiertos, porciones al ojo y cero
                ganancia.
              </p>
              <div className="p-4 bg-white/10 rounded-xl border border-white/10 relative z-10" data-id="element-41">
                <p className="font-semibold text-red-300" data-id="element-42">
                  "No necesitas inspiración. Necesitas un sistema."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. UNIQUE MECHANISM */}
      <section className="py-20 px-4 bg-[#1F1F1F] text-white overflow-hidden" data-id="element-43">
        <div className="max-w-5xl mx-auto" data-id="element-44">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-45">
            <span className="text-[#DC2626] font-bold tracking-wider uppercase text-sm" data-id="element-46">
              La Solución
            </span>
            <h2 className="text-3xl md:text-5xl font-black mt-2 mb-6" data-id="element-47">
              Sistema Combo-Listo™
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-48">
              En lugar de recetas sueltas, usamos una fórmula que convierte
              cocina casera en un menú tipo cadena.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative" data-id="element-49">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-gray-700 -translate-y-1/2 z-0" data-id="element-50"></div>

            {[{
            icon: ChefHat,
            title: 'Proteína Callejera',
            desc: 'Lo que llena',
            color: 'bg-orange-500'
          }, {
            icon: Droplet,
            title: 'Salsa Firma',
            desc: 'Lo que engancha',
            color: 'bg-red-600'
          }, {
            icon: Utensils,
            title: 'Acompañamiento',
            desc: 'Sube valor percibido',
            color: 'bg-yellow-500'
          }, {
            icon: CupSoda,
            title: 'Bebida',
            desc: 'Aumenta ticket',
            color: 'bg-blue-500'
          }].map((item, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="relative z-10 bg-gray-800 border border-gray-700 p-6 rounded-2xl text-center hover:transform hover:-translate-y-2 transition-transform duration-300 shadow-xl" data-id="element-51">
                <div className={`w-16 h-16 ${item.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`} data-id="element-52">
                  <item.icon className="w-8 h-8 text-white" data-id="element-53" />
                </div>
                <h3 className="font-bold text-lg mb-1" data-id="element-54">{item.title}</h3>
                <p className="text-gray-400 text-sm" data-id="element-55">{item.desc}</p>
                {idx < 3 && <div className="md:hidden absolute -bottom-6 left-1/2 -translate-x-1/2 text-gray-600 font-bold text-xl" data-id="element-56">
                    +
                  </div>}
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.6
        }} className="mt-12 text-center bg-gray-800/50 p-6 rounded-2xl border border-gray-700" data-id="element-57">
            <p className="text-xl font-medium" data-id="element-58">
              = <span className="text-[#DC2626] font-bold" data-id="element-59">10 combos</span> que
              la gente entiende, elige rápido y repite.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 4. TRANSFORMATION */}
      <section className="py-20 px-4 bg-[#FFFBF0]" data-id="element-60">
        <div className="max-w-5xl mx-auto" data-id="element-61">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-62">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} data-id="element-63">
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6" data-id="element-64">
                ¿Qué cambia cuando vendes combos?
              </h2>
              <div className="space-y-6" data-id="element-65">
                {[{
                title: 'Pedidos Claros',
                desc: "Tus clientes dicen 'dame el Combo #3' sin mil preguntas."
              }, {
                title: 'Producción por Tandas',
                desc: 'Cocinas componentes en bloque, no plato por plato. Adiós caos.'
              }, {
                title: 'Compras Exactas',
                desc: 'Sabes exactamente qué comprar. Cero desperdicio de ingredientes.'
              }, {
                title: 'Imagen Profesional',
                desc: 'Tu menú se ve como una cadena real, aunque cocines en casa.'
              }, {
                title: 'Ticket Alto',
                desc: 'Vendes menú completo, no solo un plato suelto. Ganas más por pedido.'
              }].map((benefit, idx) => <div key={idx} className="flex gap-4" data-id="element-66">
                    <div className="bg-green-100 p-2 rounded-full h-fit shrink-0" data-id="element-67">
                      <Check className="w-5 h-5 text-green-600" data-id="element-68" />
                    </div>
                    <div data-id="element-69">
                      <h4 className="font-bold text-gray-900" data-id="element-70">
                        {benefit.title}
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-71">{benefit.desc}</p>
                    </div>
                  </div>)}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white p-8 rounded-3xl shadow-2xl border border-gray-100 rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-72">
              <div className="space-y-4" data-id="element-73">
                <div className="flex items-center gap-3 border-b pb-4" data-id="element-74">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm" data-id="element-75">
                    MC
                  </div>
                  <div data-id="element-76">
                    <p className="font-semibold text-gray-900" data-id="element-77">María Carmen</p>
                    <p className="text-xs text-gray-500" data-id="element-78">Cliente satisfecha</p>
                  </div>
                </div>
                {/* Chat simulation */}
                <div className="space-y-3" data-id="element-79">
                  <div className="bg-gray-100 p-3 rounded-tr-2xl rounded-bl-2xl rounded-br-2xl max-w-[80%]" data-id="element-80">
                    <p className="text-sm text-gray-600" data-id="element-81">
                      Hola, ¿qué precio tiene el pollo?
                    </p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-tl-2xl rounded-bl-2xl rounded-br-2xl max-w-[90%] ml-auto border border-red-100" data-id="element-82">
                    <p className="text-sm text-gray-800 font-medium" data-id="element-83">
                      ¡Hola! Te recomiendo el{' '}
                      <span className="text-red-600 font-bold" data-id="element-84">
                        Combo #2 (Pollo Crispy)
                      </span>
                      . Incluye papas, salsa de la casa y bebida por $8.50
                      🍗🍟🥤
                    </p>
                  </div>
                  <div className="bg-gray-100 p-3 rounded-tr-2xl rounded-bl-2xl rounded-br-2xl max-w-[80%]" data-id="element-85">
                    <p className="text-sm text-gray-600" data-id="element-86">
                      ¡Uff se ve buenísimo! Dame 2 combos por favor.
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t text-center" data-id="element-87">
                  <p className="text-xs text-gray-400 uppercase tracking-widest" data-id="element-88">
                    Venta cerrada en 2 mensajes
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 5. WHAT'S INCLUDED */}
      <section className="py-20 px-4 bg-white" data-id="element-89">
        <div className="max-w-6xl mx-auto" data-id="element-90">
          <div className="text-center mb-16" data-id="element-91">
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-4" data-id="element-92">
              Lo que incluye{' '}
              <span className="text-[#DC2626]" data-id="element-93">Sabor Callejero</span>
            </h2>
            <p className="text-xl text-gray-600" data-id="element-94">
              Todo lo que necesitas para arrancar en 30 días
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8" data-id="element-95">
            {/* Main Product */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="lg:col-span-3 bg-gray-900 text-white rounded-3xl p-8 md:p-12 relative overflow-hidden shadow-2xl" data-id="element-96">
              <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/20 rounded-full blur-3xl" data-id="element-97"></div>

              <div className="relative z-10" data-id="element-98">
                <div className="flex items-center gap-3 mb-6" data-id="element-99">
                  <div className="bg-[#DC2626] p-2 rounded-lg" data-id="element-100">
                    <ShoppingBag className="w-6 h-6 text-white" data-id="element-101" />
                  </div>
                  <h3 className="text-2xl font-bold" data-id="element-102">
                    Ebook Principal (Plan Completo)
                  </h3>
                </div>

                <div className="grid md:grid-cols-2 gap-6" data-id="element-103">
                  <ul className="space-y-4" data-id="element-104">
                    {['Menú corto listo (10 combos base)', '3 salsas firma con variantes', 'Producción por tandas (batching)', 'Costeo fácil para proteger tu margen'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-105">
                        <Check className="w-5 h-5 text-[#DC2626] shrink-0 mt-0.5" data-id="element-106" />
                        <span className="text-gray-300" data-id="element-107">{item}</span>
                      </li>)}
                  </ul>
                  <ul className="space-y-4" data-id="element-108">
                    {['Presentación tipo cadena (empaque, fotos)', 'Operación sin caos: pedidos y horarios', 'Plan de 30 días (día por día)', 'Guía de lanzamiento rápido'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-109">
                        <Check className="w-5 h-5 text-[#DC2626] shrink-0 mt-0.5" data-id="element-110" />
                        <span className="text-gray-300" data-id="element-111">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. WHO IT'S FOR */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-112">
        <div className="max-w-4xl mx-auto" data-id="element-113">
          <h2 className="text-3xl font-black text-center mb-12" data-id="element-114">
            ¿Es para ti?
          </h2>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-115">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white p-8 rounded-3xl shadow-lg border-t-4 border-green-500" data-id="element-116">
              <h3 className="text-xl font-bold text-green-700 mb-6 flex items-center gap-2" data-id="element-117">
                <Check className="w-6 h-6" data-id="element-118" /> ESTO ES PARA TI SI...
              </h3>
              <ul className="space-y-4" data-id="element-119">
                {['Necesitas ingreso extra real y quieres arrancar sin caos', 'Eres principiante y quieres un paso a paso exacto', 'Estás cansada de PDFs lindos que no venden nada', 'Quieres vender por encargo y controlar tu tiempo'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-120">
                    <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" data-id="element-121" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white p-8 rounded-3xl shadow-lg border-t-4 border-red-500" data-id="element-122">
              <h3 className="text-xl font-bold text-red-700 mb-6 flex items-center gap-2" data-id="element-123">
                <X className="w-6 h-6" data-id="element-124" /> NO ES PARA TI SI...
              </h3>
              <ul className="space-y-4" data-id="element-125">
                {['Buscas resultados mágicos sin cocinar ni trabajar', 'Quieres abrir un restaurante completo mañana mismo', 'No estás dispuesta a seguir un plan paso a paso', 'Crees que ya lo sabes todo sobre ventas'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-126">
                    <X className="w-5 h-5 text-red-500 shrink-0 mt-0.5" data-id="element-127" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 7. OFFER SECTION */}
      <section id="offer" className="py-24 px-4 bg-[#1F1F1F] text-white relative overflow-hidden" data-id="element-128">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" data-id="element-129"></div>

        <div className="max-w-3xl mx-auto relative z-10" data-id="element-130">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} className="bg-white text-gray-900 rounded-3xl p-8 md:p-12 shadow-2xl text-center border-4 border-[#DC2626]" data-id="element-131">
            <div className="inline-block bg-yellow-400 text-yellow-900 font-bold px-4 py-1 rounded-full text-sm mb-6 uppercase tracking-wider" data-id="element-132">
              Oferta Especial
            </div>

            <h2 className="text-3xl md:text-4xl font-black mb-2" data-id="element-133">
              Sabor Callejero
            </h2>
            <p className="text-xl text-gray-600 mb-8" data-id="element-134">
              Menú Viral de Combos (30 días)
            </p>

            <div className="flex items-center justify-center gap-4 mb-8" data-id="element-135">
              <span className="text-gray-400 text-2xl line-through decoration-red-500 decoration-2" data-id="element-136">
                $47
              </span>
              <span className="text-6xl md:text-7xl font-black text-[#DC2626]" data-id="element-137">
                $6.39
              </span>
            </div>

            <div className="space-y-4 mb-10 text-left max-w-md mx-auto bg-gray-50 p-6 rounded-xl" data-id="element-138">
              <div className="flex items-center gap-3" data-id="element-139">
                <div className="bg-green-100 p-1 rounded-full" data-id="element-140">
                  <Check className="w-4 h-4 text-green-600" data-id="element-141" />
                </div>
                <span className="font-medium" data-id="element-142">
                  Acceso inmediato (100% Digital)
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-143">
                <div className="bg-green-100 p-1 rounded-full" data-id="element-144">
                  <Check className="w-4 h-4 text-green-600" data-id="element-145" />
                </div>
                <span className="font-medium" data-id="element-146">Todos los bonos incluidos</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-147">
                <div className="bg-green-100 p-1 rounded-full" data-id="element-148">
                  <Check className="w-4 h-4 text-green-600" data-id="element-149" />
                </div>
                <span className="font-medium" data-id="element-150">Plan paso a paso de 30 días</span>
              </div>
            </div>

            <a href="https://oriammarketing.com/cart/45497923403974:1" target="_blank" rel="noopener noreferrer" onClick={handleAddToCart} className="w-full bg-[#DC2626] hover:bg-red-700 text-white font-black text-xl md:text-2xl py-6 px-8 rounded-xl shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all mb-6 flex items-center justify-center gap-3" data-id="element-151">
              QUIERO EMPEZAR HOY
              <ArrowRight className="w-6 h-6" data-id="element-152" />
            </a>

            <div className="flex items-center justify-center gap-2 text-sm text-gray-500" data-id="element-153">
              <ShieldCheck className="w-5 h-5 text-green-500" data-id="element-154" />
              <p data-id="element-155">
                Garantía de 7 días: si no es para ti, te devolvemos tu dinero.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 8. FINAL CTA */}
      <section className="py-20 px-4 bg-white text-center" data-id="element-156">
        <div className="max-w-2xl mx-auto" data-id="element-157">
          <h2 className="text-3xl font-bold mb-6" data-id="element-158">
            En los próximos 30 días vas a tener algo que hoy te falta:
          </h2>
          <p className="text-xl text-gray-600 mb-8" data-id="element-159">
            Un sistema claro que convierte tu cocina en pedidos reales.
          </p>
          <button onClick={scrollToOffer} className="inline-flex items-center gap-2 text-[#DC2626] font-bold text-lg hover:underline underline-offset-4" data-id="element-160">
            Ir a la oferta <ArrowRight className="w-5 h-5" data-id="element-161" />
          </button>
        </div>
      </section>

      <footer className="bg-gray-900 text-gray-400 py-12 px-4" data-id="element-162">
        <div className="max-w-4xl mx-auto" data-id="element-163">
          <div className="flex flex-col md:flex-row justify-center items-center gap-6 mb-8" data-id="element-164">
            <button onClick={() => setShowPrivacy(true)} className="hover:text-white transition-colors" data-id="element-165">
              Política de Privacidad
            </button>
            <span className="hidden md:inline text-gray-600" data-id="element-166">|</span>
            <button onClick={() => setShowTerms(true)} className="hover:text-white transition-colors" data-id="element-167">
              Términos y Condiciones
            </button>
          </div>

          <div className="text-center text-sm" data-id="element-168">
            <p data-id="element-169">
              © {new Date().getFullYear()} Sabor Callejero. Todos los derechos
              reservados.
            </p>
            <p className="mt-2 text-gray-500" data-id="element-170">
              Este producto no garantiza resultados económicos. El éxito depende
              de tu implementación.
            </p>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <AnimatePresence data-id="element-171">
        {showPrivacy && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPrivacy(false)} data-id="element-172">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-173">
              <div className="sticky top-0 bg-white border-b border-gray-100 p-6 flex items-center justify-between" data-id="element-174">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-175">
                  Política de Privacidad
                </h3>
                <button onClick={() => setShowPrivacy(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-176">
                  <X className="w-5 h-5 text-gray-500" data-id="element-177" />
                </button>
              </div>
              <div className="p-6 space-y-4 text-gray-600 text-sm leading-relaxed" data-id="element-178">
                <p data-id="element-179">
                  Esta política describe cómo recopilamos, usamos y protegemos
                  la información cuando utilizas nuestros servicios digitales.
                </p>
                <p data-id="element-180">
                  <strong className="text-gray-800" data-id="element-181">
                    Información recopilada:
                  </strong>{' '}
                  Podemos recopilar datos como nombre, correo electrónico y
                  datos de pago cuando realizas una compra. Esta información se
                  utiliza únicamente para procesar tu pedido y enviarte el
                  producto digital.
                </p>
                <p data-id="element-182">
                  <strong className="text-gray-800" data-id="element-183">Uso de cookies:</strong>{' '}
                  Utilizamos cookies para mejorar tu experiencia de navegación y
                  analizar el tráfico del sitio. Puedes desactivar las cookies
                  en la configuración de tu navegador.
                </p>
                <p data-id="element-184">
                  <strong className="text-gray-800" data-id="element-185">
                    Protección de datos:
                  </strong>{' '}
                  Implementamos medidas de seguridad para proteger tu
                  información personal. No vendemos ni compartimos tus datos con
                  terceros, excepto cuando sea necesario para procesar pagos.
                </p>
                <p data-id="element-186">
                  <strong className="text-gray-800" data-id="element-187">Tus derechos:</strong>{' '}
                  Tienes derecho a acceder, rectificar o eliminar tus datos
                  personales. Para ejercer estos derechos, utiliza los canales
                  de comunicación disponibles.
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Terms and Conditions Modal */}
      <AnimatePresence data-id="element-188">
        {showTerms && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowTerms(false)} data-id="element-189">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-190">
              <div className="sticky top-0 bg-white border-b border-gray-100 p-6 flex items-center justify-between" data-id="element-191">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-192">
                  Términos y Condiciones
                </h3>
                <button onClick={() => setShowTerms(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-193">
                  <X className="w-5 h-5 text-gray-500" data-id="element-194" />
                </button>
              </div>
              <div className="p-6 space-y-4 text-gray-600 text-sm leading-relaxed" data-id="element-195">
                <p data-id="element-196">
                  Al adquirir este producto digital, aceptas los siguientes
                  términos y condiciones de uso.
                </p>
                <p data-id="element-197">
                  <strong className="text-gray-800" data-id="element-198">Producto digital:</strong>{' '}
                  Este es un producto 100% digital. Una vez completada la
                  compra, recibirás acceso inmediato al contenido. No se
                  realizan envíos físicos.
                </p>
                <p data-id="element-199">
                  <strong className="text-gray-800" data-id="element-200">Licencia de uso:</strong> La
                  compra te otorga una licencia personal e intransferible. No
                  está permitido redistribuir, revender o compartir el contenido
                  con terceros.
                </p>
                <p data-id="element-201">
                  <strong className="text-gray-800" data-id="element-202">
                    Garantía de devolución:
                  </strong>{' '}
                  Ofrecemos una garantía de 7 días. Si el producto no cumple tus
                  expectativas, puedes solicitar un reembolso completo dentro de
                  este período.
                </p>
                <p data-id="element-203">
                  <strong className="text-gray-800" data-id="element-204">Resultados:</strong> Los
                  resultados pueden variar según la implementación individual.
                  Este producto proporciona información y estrategias, pero el
                  éxito depende del esfuerzo y dedicación de cada persona.
                </p>
                <p data-id="element-205">
                  <strong className="text-gray-800" data-id="element-206">
                    Propiedad intelectual:
                  </strong>{' '}
                  Todo el contenido está protegido por derechos de autor. Queda
                  prohibida la reproducción total o parcial sin autorización
                  expresa.
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}